import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axiosInstance from '../api/axiosInstance';
import { getStoredToken, getStoredUser, saveStored, clearStored } from '../utils/authStorage';

export interface AuthUser {
  id: number;
  name: string;
  email: string;
  avatarUrl?: string | null;
  isOnline: boolean;
  lastSeen: string;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: (idToken: string, inviteToken?: string | null) => Promise<void>;
  logout: () => Promise<void>;
  setError: (err: string | null) => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface ProductionUserDto {
  userId: number;
  userName: string;
  email: string;
  createdDate: string;
  isActive: boolean;
}

function mapProductionUser(user: ProductionUserDto): AuthUser {
  return {
    id: user.userId,
    name: user.userName,
    email: user.email,
    avatarUrl: null,
    isOnline: user.isActive,
    lastSeen: user.createdDate || '',
  };
}

function loadStored(): { token: string | null; user: AuthUser | null } {
  const t = getStoredToken();
  const u = getStoredUser();
  return { token: t, user: u as AuthUser | null };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const { token, user } = loadStored();
    return { token, user, loading: !!token, error: null };
  });

  const login = useCallback(async (idToken: string, inviteToken?: string | null) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const payload: { idToken: string; inviteToken?: string } = { idToken };
      if (inviteToken) payload.inviteToken = inviteToken;
      const { data } = await axiosInstance.post<{
        accessToken: string;
        expiresAtUtc: string;
        user: ProductionUserDto;
      }>('/api/v2/auth/google', payload);

      const user = mapProductionUser(data.user);
      saveStored(data.accessToken, user);
      setState({ token: data.accessToken, user, loading: false, error: null });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Login failed.';
      setState((s) => ({ ...s, loading: false, error: msg }));
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axiosInstance.post('/api/auth/logout', {}, {
        headers: { Authorization: `Bearer ${state.token}` },
      });
    } catch {
      /* ignore */
    }
    clearStored();
    setState({ token: null, user: null, loading: false, error: null });
  }, [state.token]);

  const refreshUser = useCallback(async () => {
    if (!state.token) return;
    try {
      const { data } = await axiosInstance.get<ProductionUserDto>('/api/v2/auth/me', { headers: { Authorization: `Bearer ${state.token}` } });
      const user = mapProductionUser(data);
      saveStored(state.token, user);
      setState((s) => ({ ...s, user }));
    } catch {
      clearStored();
      setState({ token: null, user: null, loading: false, error: null });
    }
  }, [state.token]);

  useEffect(() => {
    if (!state.token) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    refreshUser().finally(() => setState((s) => ({ ...s, loading: false })));
  }, []);

  useEffect(() => {
    const handler = () => {
      clearStored();
      setState({ token: null, user: null, loading: false, error: null });
    };
    window.addEventListener('auth:logout', handler);
    return () => window.removeEventListener('auth:logout', handler);
  }, []);

  const setError = useCallback((err: string | null) => {
    setState((s) => ({ ...s, error: err }));
  }, []);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    setError,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

